import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bell, Trash2, Plus, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
const RADIUS_OPTIONS = [
  { value: 0, label: 'Exact suburb only' },
  { value: 5, label: 'Within 5 km' },
  { value: 10, label: 'Within 10 km' },
  { value: 20, label: 'Within 20 km' },
  { value: 50, label: 'Within 50 km' },
];

export const SuburbAlertsTab = ({ getAuthConfig }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('VIC');
  const [maxRent, setMaxRent] = useState('');
  const [radiusKm, setRadiusKm] = useState(0);
  const [channel, setChannel] = useState('email');
  const [creating, setCreating] = useState(false);

  // Suburb autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/suburb-alerts`, getAuthConfig());
      setAlerts(res.data?.alerts || []);
    } catch (e) {
      if (e?.response?.status !== 401) {
        console.error('Failed to load suburb alerts:', e);
        toast.error('Could not load your suburb alerts. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Debounced suburb autocomplete
  useEffect(() => {
    if (!suburb || suburb.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/public/suburbs`, {
          params: { q: suburb.trim(), state, limit: 8 },
        });
        setSuggestions(res.data?.suburbs || []);
      } catch (e) {
        setSuggestions([]);
      }
    }, 180);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [suburb, state]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!suburb.trim()) {
      toast.error('Enter a suburb');
      return;
    }
    setCreating(true);
    try {
      await axios.post(
        `${API}/suburb-alerts`,
        {
          suburb: suburb.trim(),
          state,
          max_weekly_rent: maxRent ? Number(maxRent) : null,
          radius_km: Number(radiusKm) || 0,
          channel,
        },
        getAuthConfig()
      );
      const radiusMsg = Number(radiusKm) > 0 ? ` (within ${radiusKm} km)` : '';
      toast.success(`Alert created for ${suburb}, ${state}${radiusMsg}`);
      setSuburb('');
      setMaxRent('');
      setRadiusKm(0);
      setChannel('email');
      setSuggestions([]);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to create alert';
      toast.error(detail);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (alertId) => {
    if (!window.confirm('Remove this suburb alert?')) return;
    try {
      await axios.delete(`${API}/suburb-alerts/${alertId}`, getAuthConfig());
      toast.success('Alert removed');
      setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
    } catch (e) {
      console.error('Failed to remove suburb alert:', e);
      toast.error('Failed to remove alert');
    }
  };

  return (
    <div data-testid="suburb-alerts-tab" style={{ padding: '24px 0' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: '#1a2332', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Bell size={22} color="#2563eb" /> Suburb Alerts
        </h2>
        <p style={{ color: '#6b7280', fontSize: 15, margin: 0 }}>
          Get an email the moment a new property lands in a suburb you care about.
        </p>
      </div>

      {/* Create form */}
      <form
        onSubmit={handleAdd}
        data-testid="suburb-alert-form"
        style={{
          background: '#f8fafc',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
          display: 'grid',
          gridTemplateColumns: '1.8fr 0.7fr 1.1fr 0.9fr 1fr auto',
          gap: 12,
          alignItems: 'end',
        }}
      >
        <div style={{ position: 'relative' }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Suburb</label>
          <input
            type="text"
            value={suburb}
            onChange={(e) => { setSuburb(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            placeholder="Start typing… e.g. Bendigo"
            autoComplete="off"
            data-testid="suburb-alert-input"
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 15 }}
          />
          {showSuggestions && suggestions.length > 0 && (
            <div
              data-testid="suburb-alert-suggestions"
              style={{
                position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                background: 'white', border: '1px solid #d1d5db', borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)', zIndex: 20, maxHeight: 260, overflowY: 'auto',
              }}
            >
              {suggestions.map(s => (
                <div
                  key={`${s.suburb}-${s.state}-${s.postcode}`}
                  data-testid={`suggestion-${s.suburb.toLowerCase().replace(/\s+/g,'-')}-${s.state}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setSuburb(s.suburb);
                    setState(s.state);
                    setShowSuggestions(false);
                    setSuggestions([]);
                  }}
                  style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: 14 }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <span style={{ fontWeight: 600, color: '#1a2332' }}>{s.suburb}</span>
                  <span style={{ color: '#6b7280', marginLeft: 8 }}>{s.state} · {s.postcode}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>State</label>
          <select
            value={state}
            onChange={(e) => setState(e.target.value)}
            data-testid="suburb-alert-state"
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 15, background: 'white' }}
          >
            {STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Radius</label>
          <select
            value={radiusKm}
            onChange={(e) => setRadiusKm(Number(e.target.value))}
            data-testid="suburb-alert-radius"
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 15, background: 'white' }}
          >
            {RADIUS_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Max $/wk (optional)</label>
          <input
            type="number"
            value={maxRent}
            onChange={(e) => setMaxRent(e.target.value)}
            placeholder="e.g. 250"
            data-testid="suburb-alert-maxrent"
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 15 }}
          />
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Notify by</label>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            data-testid="suburb-alert-channel"
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 15, background: 'white' }}
          >
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="both">Email + SMS</option>
          </select>
        </div>
        <button
          type="submit"
          disabled={creating}
          data-testid="suburb-alert-submit"
          style={{
            background: '#2563eb',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 15,
            cursor: creating ? 'not-allowed' : 'pointer',
            opacity: creating ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            height: 42,
          }}
        >
          <Plus size={16} /> {creating ? 'Adding…' : 'Add Alert'}
        </button>
      </form>

      {/* List */}
      {loading ? (
        <div style={{ color: '#6b7280' }}>Loading alerts…</div>
      ) : alerts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: 12, color: '#6b7280' }}>
          <Bell size={32} style={{ margin: '0 auto 8px', display: 'block', color: '#9ca3af' }} />
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No alerts yet</div>
          <div style={{ fontSize: 14 }}>Add a suburb above to be notified the moment a matching property is added.</div>
        </div>
      ) : (
        <div data-testid="suburb-alerts-list" style={{ display: 'grid', gap: 10 }}>
          {alerts.map(a => (
            <div
              key={a.alert_id}
              data-testid={`suburb-alert-${a.alert_id}`}
              style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                padding: '14px 18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <MapPin size={18} color="#2563eb" />
                <div>
                  <div style={{ fontWeight: 700, color: '#1a2332' }}>
                    {a.suburb}, {a.state}
                    {a.radius_km ? (
                      <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 700, color: '#2563eb', background: '#dbeafe', padding: '2px 8px', borderRadius: 999 }}>
                        within {a.radius_km} km
                      </span>
                    ) : null}
                    {a.channel && a.channel !== 'email' ? (
                      <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 700, color: '#065f46', background: '#d1fae5', padding: '2px 8px', borderRadius: 999 }}>
                        {a.channel === 'sms' ? 'SMS' : 'Email + SMS'}
                      </span>
                    ) : null}
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    {a.max_weekly_rent ? `Under $${a.max_weekly_rent}/wk` : 'Any weekly rent'}
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleDelete(a.alert_id)}
                data-testid={`delete-alert-${a.alert_id}`}
                style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 6 }}
                title="Remove alert"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SuburbAlertsTab;
